import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from '../../lib/routing';
import { onboardingDoneTarget, onboardingHref, safeTeacherNext } from '../../lib/onboarding-return';
import { api } from '../../lib/api';
import { authClient } from '../../lib/auth-client';

type Classroom = {
  classroom_teacher_id: string;
  name: string;
  level?: string | null;
  subject?: string | null;
  student_count: number;
};

function ClassroomCard({ classroom }: { classroom: Classroom }) {
  const subject = classroom.subject || 'Matière non renseignée';
  const count = classroom.student_count;

  return (
    <div className="flex flex-row bg-card rounded-[16px] border-[1.5px] border-border px-4 py-4 gap-[14px] items-center shadow-card">
      <div className="w-11 h-11 shrink-0 rounded-[12px] bg-naia-sky flex items-center justify-center text-[22px]" aria-hidden>📚</div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="font-extrabold text-[15px] text-foreground">{classroom.name}</div>
        <div className="flex flex-wrap gap-[6px]">
          {classroom.level ? <span className="font-semibold text-xs text-muted-foreground bg-background px-2 py-0.5 rounded-[6px]">{classroom.level}</span> : null}
          <span className="font-semibold text-xs text-muted-foreground bg-background px-2 py-0.5 rounded-[6px]">{subject}</span>
        </div>
        <div className="font-semibold text-xs text-muted-foreground">
          {count === 0 ? 'Aucun élève' : count === 1 ? '1 élève' : `${count} élèves`}
        </div>
      </div>
    </div>
  );
}

export default function TeacherOnboardingClasses() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next');
  const next = safeTeacherNext(rawNext);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.onboarding.status.query();
        if (cancelled) return;
        if (!status || status.onboarding_completed) {
          navigate(onboardingDoneTarget(rawNext), { replace: true });
          return;
        }
        setClassrooms((status.classrooms as Classroom[]) ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, rawNext]);

  const handleContinue = async () => {
    setBusy(true);
    try {
      await api.onboarding.advanceToPrefs.mutate();
      navigate(onboardingHref('/onboarding/preferences', next));
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.signOut();
    navigate('/');
  };

  return (
    <div className="flex flex-col min-h-[100dvh] pt-[max(0px,env(safe-area-inset-top))] pr-[max(20px,env(safe-area-inset-right))] pb-[max(24px,env(safe-area-inset-bottom))] pl-[max(20px,env(safe-area-inset-left))] bg-secondary gap-0">
      <div className="flex items-center justify-between py-3 shrink-0">
        <button type="button" onClick={() => navigate(-1)} className="w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer text-[22px] text-foreground font-bold rounded-sm" aria-label="Retour">
          ←
        </button>
        <h1 className="font-extrabold text-[18px] text-foreground m-0">Vos classes</h1>
        <div className="w-11" />
      </div>

      <div className="flex-1 flex flex-col gap-3 py-2 pb-4 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center flex-1 p-10">
            <div className="w-10 h-10 rounded-full border-[3px] border-border border-t-naia-blue [animation:spin_0.8s_linear_infinite]" aria-label="Chargement…" />
          </div>
        ) : classrooms.length === 0 ? (
          <div className="bg-naia-info-bg rounded-[14px] px-5 py-5 mt-6">
            <p className="font-semibold text-sm text-foreground leading-[1.6] text-center m-0">
              Vos classes n'ont pas encore été configurées — rapprochez-vous de votre administration.
            </p>
          </div>
        ) : (
          classrooms.map((c) => (
            <ClassroomCard key={c.classroom_teacher_id} classroom={c} />
          ))
        )}
      </div>

      <button
        type="button"
        onClick={handleContinue}
        disabled={busy || loading}
        className="w-full px-5 py-4 bg-primary text-primary-foreground border-none rounded-full font-extrabold text-[16px] cursor-pointer min-h-[56px] tracking-[0.3px] mt-4 transition-opacity disabled:opacity-[0.75] disabled:cursor-not-allowed"
      >
        {busy ? 'En cours…' : 'Continuer'}
      </button>
      <button
        type="button"
        onClick={() => void handleSignOut()}
        className="bg-transparent border-none font-semibold text-xs text-muted-foreground underline cursor-pointer py-2 min-h-[44px] mx-auto"
      >
        Se déconnecter
      </button>
    </div>
  );
}
